/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import Dashboard, { DashboardSection, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import { QueryWrapper } from '@g17eco/molecules/query/QueryWrapper';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { TimeRangeSelector } from '@g17eco/molecules/time-range-selector';
import { useGetDataTransferComparisonQuery, useGetInitiativeAppIntegrationQuery, useListSubConnectionsQuery, useLazyGetDataTransferExportQuery } from '@api/initiative-app-integrations';
import { useAppSelector } from '@reducers/index';
import { isStaff } from '@selectors/user';
import { CTAdminBreadcrumbs } from '../../../components/breadcrumbs/CTAdminBreadcrumbs';
import { ROUTES } from '@constants/routes';
import { generateUrl } from '@routes/util';
import { DataTransferTable } from './DataTransferTable';
import { DataTransferQuestionView } from './DataTransferQuestionView';
import { downloadBlob } from '@utils/downloadBlob';
import { DataTransferCsvExport } from './DataTransferCsvExport';
import { useDataTransferParams } from './useDataTransferParams';
import { applyFilters } from './data-transfer-utils';
import { DataTransferFilterBar } from './DataTransferFilterBar';
import { DataTransferSummaryCards } from './DataTransferSummaryCards';

export const DataTransferPage = () => {
  const { code, initiativeId } = useParams<{ code: string; initiativeId: string }>();
  const isUserStaff = useAppSelector(isStaff);
  const { childInitiativeId, startDate, endDate, dateRange, timeRange, groupMode, search, filters, setChildInitiativeId, setDateRange, setGroupMode, setSearch, setFilter, clearFilters } = useDataTransferParams();

  const integrationQuery = useGetInitiativeAppIntegrationQuery({ initiativeId, code }, { skip: !code });
  const providerName = integrationQuery.data?.provider?.name;

  const subConnectionsQuery = useListSubConnectionsQuery(
    { initiativeId, code },
    { skip: !code || !isUserStaff },
  );

  const canQuery = Boolean(isUserStaff && childInitiativeId && startDate && endDate);

  const comparisonQuery = useGetDataTransferComparisonQuery(
    { initiativeId, code, childInitiativeId, startDate, endDate },
    { skip: !canQuery },
  );

  const [triggerExport, exportQuery] = useLazyGetDataTransferExportQuery();

  const [exportError, setExportError] = useState<string>();

  const handleExport = useCallback(async () => {
    if (!canQuery) {
      return;
    }
    setExportError(undefined);
    const result = await triggerExport({ initiativeId, code, childInitiativeId, startDate, endDate });
    if (result.data) {
      downloadBlob(result.data, `data-transfer-${code}.xlsx`);
    }
    if (result.error) {
      setExportError('Failed to download export. Please try again.');
    }
  }, [triggerExport, initiativeId, code, childInitiativeId, startDate, endDate, canQuery]);

  if (!isUserStaff) {
    return null;
  }

  return (
    <Dashboard className='w-100'>
      <div className='pl-2 pb-4'>
        <CTAdminBreadcrumbs
          initiativeId={initiativeId}
          breadcrumbs={[
            { label: 'Connections', url: generateUrl(ROUTES.APP_INTEGRATIONS, { initiativeId }) },
            { label: providerName ?? code, url: generateUrl(ROUTES.APP_INTEGRATIONS_VIEW, { initiativeId, code }) },
            { label: 'Data Transfer' },
          ]}
        />
      </div>
      <DashboardSectionTitle title={<>{providerName ?? 'Data Transfer'} — Data Transfer Review <FeatureStability stability='internal' /></>} />
      <DashboardSection>
        <div className='row mb-3'>
          <div className='col-md-6'>
            <label htmlFor='subsidiary-selector' className='form-label fw-bold'>Select Subsidiary</label>
            <QueryWrapper query={subConnectionsQuery} onSuccess={(subConnections) => (
              <select
                id='subsidiary-selector'
                className='form-select'
                value={childInitiativeId}
                onChange={(e) => setChildInitiativeId(e.target.value)}
              >
                <option value=''>Select a subsidiary...</option>
                {subConnections.map((sc) => (
                  <option key={sc._id} value={sc.initiativeId}>
                    {sc.data?.facilityName ?? sc.externalId ?? sc.name}
                  </option>
                ))}
              </select>
            )} />
          </div>
          <div className='col-md-6'>
            <label className='form-label fw-bold'>Date Range</label>
            <TimeRangeSelector
              timeRange={timeRange}
              dateRange={dateRange}
              onChangeDateRange={setDateRange}
            />
          </div>
        </div>

        {canQuery && (
          <QueryWrapper query={comparisonQuery} onSuccess={(data) => {
            const totalRows = data.surveys.reduce((acc, s) => acc + s.rows.length, 0);
            const filteredSurveys = data.surveys.map((s) => ({
              ...s,
              rows: applyFilters(s.rows, filters, search),
            }));
            const filteredRowCount = filteredSurveys.reduce((acc, s) => acc + s.rows.length, 0);

            return (
              <>
                {(data.connectionExternalId || data.childExternalId) && (
                  <div className='d-flex gap-3 mb-3'>
                    {data.connectionExternalId && (
                      <small className='text-ThemeTextLight'>
                        Root External ID: <code>{data.connectionExternalId}</code>
                      </small>
                    )}
                    {data.childExternalId && (
                      <small className='text-ThemeTextLight'>
                        Child External ID: <code>{data.childExternalId}</code>
                      </small>
                    )}
                  </div>
                )}
                <DataTransferSummaryCards surveys={data.surveys} />
                <div className='d-flex align-items-center gap-3 mb-3'>
                  <div className='btn-group btn-group-sm'>
                    <button
                      className={`btn ${groupMode === 'survey' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setGroupMode('survey')}
                    >
                      <i className='fa fa-list-alt me-1' />Group by Survey
                    </button>
                    <button
                      className={`btn ${groupMode === 'question' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setGroupMode('question')}
                    >
                      <i className='fa fa-columns me-1' />Group by Question
                    </button>
                  </div>
                </div>
                <DataTransferFilterBar
                  search={search}
                  onSearch={setSearch}
                  filters={filters}
                  onSelect={setFilter}
                  onClear={clearFilters}
                  totalRows={totalRows}
                  filteredRows={filteredRowCount}
                />
                <div className='d-flex justify-content-end gap-2 mb-3'>
                  <button
                    className='btn btn-outline-secondary btn-sm'
                    onClick={() => comparisonQuery.refetch()}
                    disabled={comparisonQuery.isFetching}
                  >
                    <i className={`fa fa-refresh me-1 ${comparisonQuery.isFetching ? 'fa-spin' : ''}`} />
                    Refresh
                  </button>
                  <button
                    className='btn btn-outline-primary btn-sm'
                    onClick={handleExport}
                    disabled={exportQuery.isFetching}
                  >
                    <i className={`fa fa-download me-1 ${exportQuery.isFetching ? 'fa-spin' : ''}`} />
                    Download XLSX
                  </button>
                  <DataTransferCsvExport surveys={filteredSurveys} filename={`data-transfer-${code}.csv`} />
                  {exportError && (
                    <small className='text-ThemeDangerMedium'>
                      <i className='fa fa-exclamation-circle me-1' />{exportError}
                    </small>
                  )}
                </div>
                {groupMode === 'survey' ? (
                  filteredSurveys.map((survey) => {
                    const discrepancies = survey.rows.filter((r) => r.hasDiscrepancy).length;
                    const matching = survey.rows.filter((r) => !r.hasDiscrepancy && r.survey.value !== undefined && r.integration.value !== undefined).length;
                    return (
                      <div key={survey.surveyId || 'no-survey'} className='mb-4'>
                        <h6 className='fw-bold'>
                          {survey.label}
                          {survey.effectiveDate && (
                            <small className='text-ThemeTextLight ms-2'>
                              ({new Date(survey.effectiveDate).toLocaleDateString()})
                            </small>
                          )}
                          <span className='badge background-ThemeIconSecondary text-ThemeTextWhite ms-2'>
                            {survey.rows.length} rows
                          </span>
                          {matching > 0 && (
                            <span className='small text-ThemeSuccessMedium ms-2'>
                              <i className='fa fa-check me-1' />{matching} matching
                            </span>
                          )}
                          {discrepancies > 0 && (
                            <span className='small text-ThemeDangerMedium ms-2'>
                              <i className='fa fa-exclamation-circle me-1' />{discrepancies} differing
                            </span>
                          )}
                        </h6>
                        <DataTransferTable rows={survey.rows} childInitiativeId={childInitiativeId} />
                      </div>
                    );
                  })
                ) : (
                  <DataTransferQuestionView surveys={data.surveys} filteredSurveys={filteredSurveys} childInitiativeId={childInitiativeId} />
                )}
              </>
            );
          }} />
        )}
      </DashboardSection>
    </Dashboard>
  );
};
